/**
 * OAuthCallback Component
 * Handles the OAuth callback from Cognito Hosted UI
 */

import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthService } from '../services/AuthService';
import LoadingSpinner from './LoadingSpinner';
import './Login.css';

interface OAuthCallbackProps {
  onSuccess: () => void;
}

function OAuthCallback({ onSuccess }: OAuthCallbackProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const authService = AuthService.getInstance();
  const handledRef = useRef(false);

  useEffect(() => {
    // Prevent the effect from firing more than once — after we process the
    // OAuth code and change the URL, useSearchParams triggers a re-run
    // which would see no code and schedule a navigate('/login') after 3s.
    if (handledRef.current) return;
    handledRef.current = true;

    const handleCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');

      console.log('[OAuthCallback] Component mounted');
      console.log('[OAuthCallback] Code:', code);
      console.log('[OAuthCallback] Error:', errorParam);

      if (errorParam) {
        console.error('[OAuthCallback] OAuth error:', errorParam);
        setError(`Authentication failed: ${errorParam}`);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (!code) {
        console.error('[OAuthCallback] No authorization code found');
        setError('No authorization code received');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        console.log('[OAuthCallback] Processing authorization code...');
        const success = await authService.handleOAuthCallback();

        if (success) {
          console.log('[OAuthCallback] Authentication successful!');

          // Update auth state first
          onSuccess();

          // Check for a pending THRIFT link redirect before defaulting to /home
          const pendingRedirect =
            sessionStorage.getItem('pendingLinkRedirect') ||
            localStorage.getItem('pendingLinkRedirect');
          const destination = pendingRedirect || '/home';
          if (pendingRedirect) {
            console.log('[OAuthCallback] Found pending link redirect:', pendingRedirect);
            sessionStorage.removeItem('pendingLinkRedirect');
            localStorage.removeItem('pendingLinkRedirect');
          }

          // Small delay to ensure state updates are processed
          setTimeout(() => {
            // Clean up URL and navigate
            window.history.replaceState({}, document.title, destination);
            navigate(destination, { replace: true });
          }, 100);
        } else {
          console.error('[OAuthCallback] Authentication failed');
          setError('Authentication failed. Please try again.');
          setTimeout(() => navigate('/login'), 3000);
        }
      } catch (err) {
        console.error('[OAuthCallback] Error during callback:', err);
        setError('An error occurred during authentication.');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, onSuccess, authService]);

  if (error) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>Authentication Error</h1>
          </div>
          <div className="error-message" role="alert">
            {error}
          </div>
          <p style={{ textAlign: 'center', marginTop: '1rem' }}>
            Redirecting to login...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <LoadingSpinner size="large" message="Completing authentication..." />
      </div>
    </div>
  );
}

export default OAuthCallback;
